import { Alert, Button, Group, Loader, Menu, Modal, SegmentedControl, Stack, Text, Textarea } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { CommentVisibility, TransitionFieldValue, TransitionOption } from "@msc/shared";

import { isJiraReauthRequired } from "../../hooks/useCustomerView.js";
import { useTicketActions, useTransitionTicket } from "../../hooks/useCustomerViewActions.js";
import { writeErrorText } from "../../lib/customerView/jiraValidation.js";
import { fromLocalDateString } from "../../lib/date.js";
import { type Eingabe, istLeer, MaskenFeld, maskenwerte } from "./FeldMaske.js";
import { ReconnectBanner } from "./ReconnectBanner.js";
import { StatusBadge } from "./StatusBadge.js";

export { maskenwerte } from "./FeldMaske.js";

/**
 * Statuswechsel im Cockpit: die Übergänge, die Jira für dieses Ticket anbietet,
 * ohne die gesperrten (#335). Die Maske baut sich aus Jiras Feldern; braucht
 * ein Übergang ein Pflichtfeld, das das Cockpit nicht kann, ist er hier
 * gesperrt und verweist auf Jira. Ein done-Übergang ist gesperrt, solange ein
 * Formular offen ist — Jira würde ihn ohnehin ablehnen, aber der Lead soll es
 * vor dem Klick sehen.
 */
export function TransitionMenu({
  ticketKey,
  objectKey,
  currentStatus,
  currentDueDate,
}: {
  ticketKey: string;
  objectKey: string | null;
  currentStatus: string;
  currentDueDate: string | null;
}) {
  const { t } = useTranslation();
  const actions = useTicketActions(ticketKey);
  const apply = useTransitionTicket(objectKey);
  const [chosen, setChosen] = useState<TransitionOption | null>(null);
  const [werte, setWerte] = useState<Record<string, Eingabe>>({});
  const [vorbelegt, setVorbelegt] = useState<Record<string, Eingabe>>({});
  const [comment, setComment] = useState("");
  const [visibility, setVisibility] = useState<CommentVisibility>("public");

  const forms = actions.data?.forms ?? [];
  const openForms = forms.filter((f) => !f.submitted).length;
  const options = actions.data?.transitions ?? [];

  const open = (tr: TransitionOption) => {
    setComment("");
    setVisibility("public");
    apply.reset();
    if (tr.fields.length === 0) {
      // Kein Feld nötig — sofort übertragen, `chosen` bleibt null, damit die
      // Menü-Schaltfläche den Spinner zeigt und das untere Alert bei einem
      // Fehler erreichbar bleibt (C1).
      run(tr, {}, "");
      return;
    }
    const start: Record<string, Eingabe> =
      tr.fields.some((f) => f.key === "duedate") && currentDueDate ? { duedate: fromLocalDateString(currentDueDate) } : {};
    setWerte(start);
    setVorbelegt(start);
    setChosen(tr);
  };

  const run = (tr: TransitionOption, fields: Record<string, TransitionFieldValue>, text: string) => {
    apply.mutate(
      {
        key: ticketKey, transitionId: tr.id, toStatusCategory: tr.toStatusCategory,
        ...(Object.keys(fields).length > 0 ? { fields } : {}),
        ...(text.trim() ? { comment: { text: text.trim(), visibility } } : {}),
      },
      { onSuccess: () => setChosen(null) },
    );
  };

  const setze = (key: string, v: Eingabe) => setWerte((alt) => ({ ...alt, [key]: v }));
  const canApply = !!chosen && chosen.fields.every((f) => !f.required || !istLeer(werte[f.key]));

  return (
    <>
      <Menu shadow="md" width={260}>
        <Menu.Target>
          <Button size="xs" variant="light" loading={apply.isPending && !chosen}>
            {t("pages.customerView.cockpit.actions.changeStatus")}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {actions.isLoading && (
            <Menu.Label><Group gap={6} wrap="nowrap"><Loader size="xs" />{t("common.loading")}</Group></Menu.Label>
          )}
          {actions.isError && (
            isJiraReauthRequired(actions.error) ? <ReconnectBanner /> : (
              <Menu.Label c="red">{t("pages.customerView.cockpit.actions.saveFailed")}</Menu.Label>
            )
          )}
          {!actions.isLoading && !actions.isError && options.length === 0 && (
            <Menu.Label>{t("pages.customerView.cockpit.actions.noTransitions")}</Menu.Label>
          )}
          {options.map((tr) => {
            const blocked = tr.requiresSubmittedForms && openForms > 0;
            const nurJira = tr.jiraOnlyRequired.length > 0;
            return (
              <Menu.Item key={tr.id} disabled={blocked || nurJira || apply.isPending} onClick={() => open(tr)}>
                <Stack gap={0}>
                  <Group justify="space-between" gap="xs">
                    <Text size="sm">{tr.toStatus}</Text>
                    {blocked && <Text size="xs" c="dimmed">{t("pages.customerView.cockpit.actions.blockedByForms", { count: openForms })}</Text>}
                  </Group>
                  {nurJira && (
                    <Text size="xs" c="dimmed">
                      {t("pages.customerView.cockpit.actions.onlyInJira", {
                        fields: tr.jiraOnlyRequired.map((f) => f.name).join(", "),
                      })}
                    </Text>
                  )}
                </Stack>
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={!!chosen && chosen.fields.length > 0}
        onClose={() => { if (!apply.isPending) setChosen(null); }}
        title={chosen ? t("pages.customerView.cockpit.actions.transitionTitle", { status: chosen.toStatus }) : ""}
        closeOnClickOutside={!apply.isPending}
        withCloseButton={!apply.isPending}
      >
        {chosen && (
          <Stack gap="sm">
            <Group gap="xs"><Text size="sm">{currentStatus}</Text><Text size="sm">→</Text><StatusBadge status={chosen.toStatus} category={chosen.toStatusCategory} /></Group>
            {chosen.fields.map((f) => (
              <MaskenFeld key={f.key} feld={f} wert={werte[f.key]} onChange={(v) => setze(f.key, v)} />
            ))}
            <Textarea label={t("pages.customerView.cockpit.actions.commentOptional")} autosize minRows={2} value={comment} onChange={(e) => setComment(e.currentTarget.value)} />
            <SegmentedControl size="xs" value={visibility} onChange={(v) => setVisibility(v as CommentVisibility)}
              data={[
                { value: "public", label: t("pages.customerView.cockpit.actions.visibilityPublic") },
                { value: "internal", label: t("pages.customerView.cockpit.actions.visibilityInternal") },
              ]} />
            {apply.isError && (
              isJiraReauthRequired(apply.error) ? <ReconnectBanner /> : (
                <Alert color="red" variant="light">{writeErrorText(apply.error, t, "pages.customerView.cockpit.actions.saveFailed")}</Alert>
              )
            )}
            <Group justify="flex-end">
              <Button variant="default" size="xs" disabled={apply.isPending} onClick={() => setChosen(null)}>{t("pages.customerView.cockpit.actions.cancel")}</Button>
              <Button size="xs" loading={apply.isPending} disabled={!canApply} onClick={() => run(chosen, maskenwerte(chosen.fields, werte, vorbelegt), comment)}>
                {t("pages.customerView.cockpit.actions.apply")}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      {apply.isError && !chosen && (
        isJiraReauthRequired(apply.error) ? <ReconnectBanner /> : (
          <Alert color="red" variant="light" mt="xs">{writeErrorText(apply.error, t, "pages.customerView.cockpit.actions.saveFailed")}</Alert>
        )
      )}
    </>
  );
}
